import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './index.less';
import { Loading } from '@kim/ui';
import {
  Global03ActionGuanbiAreaIcon,
  Global03ActionSousuonewLineIcon,
} from '@kim/icon';
import classNames from 'classnames';
import { TSessionItem } from '../../type.js';
import { SessionItem } from '../session-item/index.js';
import { useAIWindowContext } from '../../../Context/context.js';
import { debounce } from 'lodash-es';
import intl from '@/internationalzation/intl';
import { getConversationSearchApi } from '@src/services/walle';
import { useLocation, useNavigate } from 'react-router-dom';
import useClawWsStore from '@src/stores/useClawWsStore';
import type { ClawSession } from '@src/stores/useClawWsStore';
import {
  buildMainagentPath,
  isMainagentPath,
} from '@src/utils/mainagent-route';
import {
  getBaseKeyModule,
  parseClawAgentSessionKey,
  useClawWs,
} from '@src/hooks/useClawWs';
import useAgentsStore from '@src/stores/useAgentsStore';
import { isClawSidebarHistoryPath } from '../../clawHistoryRoutes';

interface ISearchProps {
  onExit: () => void;
}

function getClawSessionDisplayLabel(s: ClawSession): string {
  return (
    s.label ??
    s.displayName ??
    s.display_name ??
    s.key ??
    ''
  ).toString();
}

/** 会话标题命中，或 agentList 中对应数字员工 displayName 命中 */
function clawSessionMatchesKeyword(
  s: ClawSession,
  kwLower: string,
  agentList: { marioAgentId?: string; displayName?: string }[],
): boolean {
  if (!kwLower) return false;
  if (getClawSessionDisplayLabel(s).toLowerCase().includes(kwLower)) {
    return true;
  }
  const { agentId } = parseClawAgentSessionKey(s.key);
  if (!agentId) return false;
  return agentList.some(
    (a) =>
      a.marioAgentId === agentId &&
      (a.displayName ?? '').toLowerCase().includes(kwLower),
  );
}

function getMatchedAgentName(
  s: ClawSession,
  kwLower: string,
  agentList: { marioAgentId?: string; displayName?: string }[],
): string | undefined {
  if (!kwLower) return undefined;
  if (getClawSessionDisplayLabel(s).toLowerCase().includes(kwLower)) {
    return undefined;
  }
  const { agentId } = parseClawAgentSessionKey(s.key);
  if (!agentId) return undefined;
  const hit = agentList.find(
    (a) =>
      a.marioAgentId === agentId &&
      (a.displayName ?? '').toLowerCase().includes(kwLower),
  );
  return hit?.displayName;
}

export const Search = (props: ISearchProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  /** 须与 SidebarMenu 的 Claw 历史区路由一致，否则会用对话搜索 API 而非 clawSessions */
  const isClawSearch = isClawSidebarHistoryPath(location.pathname);

  const { isDarkMode, toConversion } = useAIWindowContext();
  const { sessions: clawSessions } = useClawWsStore();
  const { agentList, fetchAgentList } = useAgentsStore();
  const { switchSession } = useClawWs();

  useEffect(() => {
    if (!isClawSearch) return;
    if (useAgentsStore.getState().agentList.length > 0) return;
    void fetchAgentList();
  }, [isClawSearch, fetchAgentList]);

  const [data, setData] = useState<TSessionItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [focus, setFocus] = useState(false);
  const [loading, setLoading] = useState(false);
  const isComposing = useRef(false);

  const handleSearch = useCallback(
    debounce(async (value: string) => {
      if (isClawSearch) return;
      setLoading(true);

      const res = await getConversationSearchApi({
        keyword: value,
      });
      if (res.data) {
        setData(res.data as unknown as TSessionItem[]);
        setLoading(false);
      }
    }, 500),
    [isClawSearch],
  );

  const clawFiltered = useMemo(() => {
    const kw = searchValue.trim().toLowerCase();
    if (!kw || !isClawSearch) return [];
    return clawSessions
      .filter((s) => clawSessionMatchesKeyword(s, kw, agentList))
      .map((s) => ({
        session: s,
        matchedAgentName: getMatchedAgentName(s, kw, agentList),
      }));
  }, [clawSessions, searchValue, isClawSearch, agentList]);

  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  const handleCompositionEnd = (e: any) => {
    isComposing.current = false;
    handleChange(e);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    if (!isClawSearch) {
      handleSearch(value);
    }
  };

  const handleFocus = () => {
    setFocus(true);
  };

  const handleBlur = () => {
    setFocus(false);
  };

  const handleExit = () => {
    setFocus(false);
    setSearchValue('');
    props.onExit();
  };

  return (
    <div className="kim-ai-mario-sidebar-search">
      <div className={classNames('search-input-wrapper', { focus })}>
        <Global03ActionSousuonewLineIcon
          size={16}
          className="search-input-icon"
          onClick={() => {
            handleSearch(searchValue);
          }}
        />
        <input
          className="search-input"
          type="text"
          placeholder={intl.t('mario.search.title')}
          value={searchValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          autoFocus={true}
        />
        <Global03ActionGuanbiAreaIcon
          size={16}
          className="exit-icon"
          onClick={handleExit}
        />
        {/* <Icon
          name="eed_web_icon_global_03_action_guanbi_area"
          size={16}
          className="exit-icon"
          onClick={handleExit}
        /> */}
      </div>
      <div className="search-result-wrapper">
        {isClawSearch ? (
          <>
            {clawFiltered.length > 0 ? (
              <div className="search-result-list">
                {clawFiltered.map(({ session, matchedAgentName }) => {
                  const title = getClawSessionDisplayLabel(session);
                  return (
                    <SessionItem
                      onClick={() => {
                        switchSession(session.key);
                        navigate(buildMainagentPath(getBaseKeyModule(session.key)));
                        props.onExit();
                      }}
                      key={session.key}
                      highlightText={searchValue}
                      matchedAgentName={matchedAgentName}
                      title={title}
                      id={session.key}
                    />
                  );
                })}
              </div>
            ) : searchValue.trim() ? (
              <div className="kim-ai-mario-sidebar-list-empty">
                {intl.t('mario.search.null')}
              </div>
            ) : null}
          </>
        ) : !loading && data.length > 0 ? (
          <div className="search-result-list">
            {data.map((item) => (
              <SessionItem
                onClick={() => {
                  toConversion(item.conversationId);
                }}
                key={item.conversationId}
                highlightText={searchValue}
                title={item.conversationName}
                icon={
                  isDarkMode ? item.icon?.dark || '' : item.icon?.light || ''
                }
                id={item.conversationId || ''}
              />
            ))}
          </div>
        ) : loading ? (
          <Loading className="mario-loading center" size={24} />
        ) : searchValue.trim() ? (
          <div className="kim-ai-mario-sidebar-list-empty">
            {intl.t('mario.search.null')}
          </div>
        ) : null}
      </div>
    </div>
  );
};
